import {
  Alert,
  Anchor,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconPrinter,
  IconReceipt,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { odpriPdf } from '../api/pdf';
import { datum, datumZaApi, denar, kolicina } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { usePravica } from '../seja/pravice';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { IzbiraOsebe } from '../vozila/IzbiraOsebe';
import { obrezano, velike, type VoziloStranke } from './delavnica';
import { PLATISCA, SEZONE, useCenikHotelaGum, type KompletGum, type PlatiscaGum, type SezonaGum } from './gume';
import { IzbiraVozila } from './IzbiraVozila';

type Oseba = Shema['OsebaPodatki'];
type Profil = 'profilSl' | 'profilSd' | 'profilZl' | 'profilZd';
const PROFILI: [Profil, string][] = [
  ['profilSl', 'Spredaj levo'],
  ['profilSd', 'Spredaj desno'],
  ['profilZl', 'Zadaj levo'],
  ['profilZd', 'Zadaj desno'],
];

type Vnos = {
  sezona: SezonaGum | null;
  znamka: string;
  vzorec: string;
  dimenzija: string;
  indeks: string;
  dot: string;
  stevilo: number | string;
  platisca: PlatiscaGum;
  lokacija: string;
  opomba: string;
  shranjeno: string | null;
  cenikId: string | null;
} & Record<Profil, number | string>;

const prazen: Vnos = {
  sezona: null,
  znamka: '',
  vzorec: '',
  dimenzija: '',
  indeks: '',
  dot: '',
  stevilo: 4,
  platisca: 'BREZ',
  lokacija: '',
  opomba: '',
  shranjeno: null,
  cenikId: null,
  profilSl: '',
  profilSd: '',
  profilZl: '',
  profilZd: '',
};

function izKompleta(g: KompletGum): Vnos {
  return {
    sezona: g.sezona ?? null,
    znamka: g.znamka ?? '',
    vzorec: g.vzorec ?? '',
    dimenzija: g.dimenzija ?? '',
    indeks: g.indeks ?? '',
    dot: g.dot ?? '',
    stevilo: g.stevilo ?? 4,
    platisca: g.platisca ?? 'BREZ',
    lokacija: g.lokacija ?? '',
    opomba: g.opomba ?? '',
    shranjeno: g.shranjeno ?? null,
    cenikId: g.cenikId != null ? String(g.cenikId) : null,
    profilSl: g.profilSl ?? '',
    profilSd: g.profilSd ?? '',
    profilZl: g.profilZl ?? '',
    profilZd: g.profilZd ?? '',
  };
}

const stevilka = (v: number | string) => (typeof v === 'number' ? v : undefined);

/**
 * Komplet gum v hotelu za gume: nov ({@code /delavnica/gume/nov}, z {@code ?vozilo=} ali {@code ?nalog=} izpolnjena
 * vozilo in stranka) ali obstoječ. Vozilo in stranka, podatki gum, profil po gumah in hramba (lokacija ni obvezna,
 * na isti lokaciji je lahko več kompletov) z neobvezno postavko cenika hotela za račun (do izdaje računa jo je mogoče
 * zamenjati). Pri obstoječem »Nalepke (PDF)«, »Stranka je prevzela gume« (potrditev) in »Izbriši«; shranjen komplet s
 * postavko, ki še ni zaračunan, ima na vrhu »Izdaj račun« (račun se običajno izda takoj ob vnosu).
 */
export function PodrobnostiKompleta() {
  const { id: idParam } = useParams();
  const nov = idParam === 'nov';
  const id = nov ? null : Number(idParam);
  const [parametri] = useSearchParams();
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  const [komplet, setKomplet] = useState<KompletGum | null>(null);
  const [v, setV] = useState<Vnos>(prazen);
  const [vozilo, setVozilo] = useState<VoziloStranke | null>(null);
  const [kupec, setKupec] = useState<Oseba | null>(null);
  const [nalogId, setNalogId] = useState<number | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [brisanje, setBrisanje] = useState(false);

  const naloziStranko = (kupecId: number | undefined | null) =>
    kupecId
      ? podatki(api.GET('/api/osebe/{vrsta}/{id}', { params: { path: { vrsta: 'kupci', id: kupecId } } }))
          .then(setKupec)
          .catch(() => undefined)
      : undefined;

  useEffect(() => {
    if (id) {
      void podatki(api.GET('/api/delavnica/gume/{id}', { params: { path: { id } } }))
        .then((g) => {
          setKomplet(g);
          setV(izKompleta(g));
          setNalogId(g.nalogId ?? null);
          setVozilo(g.voziloId ? { id: g.voziloId, registrska: g.registrska, znamka: g.vozilo } : null);
          return naloziStranko(g.kupecId);
        })
        .catch((e: unknown) => setNapaka(sporocilo(e)));
      return;
    }
    const izVozila = Number(parametri.get('vozilo')) || null;
    const izNaloga = Number(parametri.get('nalog')) || null;
    if (izNaloga) {
      void podatki(api.GET('/api/delavnica/nalogi/{id}', { params: { path: { id: izNaloga } } }))
        .then((n) => {
          setNalogId(izNaloga);
          setVozilo(n.vozilo ?? null);
          return naloziStranko(n.kupecId);
        })
        .catch((e: unknown) => setNapaka(sporocilo(e)));
    } else if (izVozila) {
      void podatki(api.GET('/api/delavnica/vozila/{id}', { params: { path: { id: izVozila } } }))
        .then((p) => {
          setVozilo(p.vozilo ?? null);
          return naloziStranko(p.vozilo?.kupecId);
        })
        .catch((e: unknown) => setNapaka(sporocilo(e)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob odprtju zaslona
  }, [idParam]);

  const nastavi = (k: Partial<Vnos>) => setV((x) => ({ ...x, ...k }));
  const osvezi = async (g: KompletGum) => {
    await odjemalec.invalidateQueries({ queryKey: ['gume'] });
    if (g.voziloId) {
      await odjemalec.invalidateQueries({ queryKey: ['vozilo-stranke', g.voziloId] });
    }
  };

  const shrani = async () => {
    setDela(true);
    setNapaka(null);
    setPolja({});
    try {
      const body = {
        kupecId: kupec?.id ?? undefined,
        voziloId: vozilo?.id ?? undefined,
        nalogId: nalogId ?? undefined,
        sezona: v.sezona ?? undefined,
        znamka: obrezano(v.znamka),
        vzorec: obrezano(v.vzorec),
        dimenzija: obrezano(v.dimenzija),
        indeks: obrezano(v.indeks),
        dot: obrezano(v.dot),
        stevilo: stevilka(v.stevilo),
        platisca: v.platisca,
        profilSl: stevilka(v.profilSl),
        profilSd: stevilka(v.profilSd),
        profilZl: stevilka(v.profilZl),
        profilZd: stevilka(v.profilZd),
        lokacija: obrezano(v.lokacija),
        opomba: obrezano(v.opomba),
        shranjeno: v.shranjeno ?? undefined,
        cenikId: v.cenikId ? Number(v.cenikId) : undefined,
      };
      const g = id
        ? await podatki(api.PUT('/api/delavnica/gume/{id}', { params: { path: { id } }, body }))
        : await podatki(api.POST('/api/delavnica/gume', { body }));
      await osvezi(g);
      notifications.show({
        color: 'green',
        message:
          (g.lokacija ? `Gume so shranjene na lokaciji ${g.lokacija}.` : 'Gume so shranjene.') +
          (g.zaRacun ? ' Zdaj lahko izdate račun.' : ''),
      });
      setKomplet(g);
      setV(izKompleta(g));
      if (!id) {
        void navigate(`/delavnica/gume/${g.id}`, { replace: true });
      }
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  const [prevzemOdprt, setPrevzemOdprt] = useState(false);
  const cenikHotela = useCenikHotelaGum();
  const prevzem = async () => {
    setDela(true);
    setNapaka(null);
    try {
      const g = await podatki(api.POST('/api/delavnica/gume/{id}/prevzem', { params: { path: { id: id! } } }));
      await osvezi(g);
      setKomplet(g);
      setV(izKompleta(g));
      notifications.show({ color: 'green', message: 'Stranka je prevzela gume.' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setPrevzemOdprt(false);
      setDela(false);
    }
  };

  const izbrisi = async () => {
    setDela(true);
    try {
      await podatki(api.DELETE('/api/delavnica/gume/{id}', { params: { path: { id: id! } } }));
      await odjemalec.invalidateQueries({ queryKey: ['gume'] });
      notifications.show({ color: 'green', message: 'Komplet gum je izbrisan.' });
      void navigate('/delavnica/gume', { replace: true });
    } catch (e) {
      setNapaka(sporocilo(e));
      setDela(false);
    }
  };

  const nalepke = async () => {
    try {
      await odpriPdf(api.GET('/api/delavnica/gume/{id}/nalepke', { params: { path: { id: id! } }, parseAs: 'blob' }));
    } catch (e) {
      setNapaka(sporocilo(e));
    }
  };

  const shranjen = komplet?.stanje === 'SHRANJEN';
  const zaracunan = komplet?.racunId != null;
  // Račun vzame shranjeno postavko, zato gumb samo, ko izbira ni spremenjena.
  const zaRacun = komplet?.zaRacun && v.cenikId === (komplet.cenikId != null ? String(komplet.cenikId) : null);
  // Postavke cenika; postavka, ki jo komplet že ima, ostane izbirna, tudi če je iz cenika odstranjena.
  const postavke = (cenikHotela.data ?? []).map((p) => ({
    value: String(p.id),
    label: `${p.naziv ?? ''} – ${denar(p.cena)} € brez DDV`,
  }));
  if (komplet?.cenikId != null && !postavke.some((p) => p.value === String(komplet.cenikId))) {
    postavke.unshift({
      value: String(komplet.cenikId),
      label: `${komplet.postavkaNaziv ?? ''} – ${denar(komplet.postavkaCena)} € brez DDV`,
    });
  }
  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/gume"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Hotel za gume
      </Button>
      <GlavaZaslona
        naslov={nov ? 'Shrani gume v hotel' : `Gume ${komplet?.lokacija ?? komplet?.registrska ?? ''}`.trim()}
        opis={[kupec ? [kupec.ime, kupec.priimek].filter(Boolean).join(' ') : null, vozilo?.registrska]
          .filter(Boolean)
          .join(' · ')}
        pomoc="hotel-za-gume"
        dejanja={
          komplet && (
            <Group gap="xs">
              <Button variant="default" leftSection={<IconPrinter size={18} />} onClick={() => void nalepke()}>
                Nalepke
              </Button>
              {shranjen && ureja && (
                <Button variant="default" leftSection={<IconCheck size={18} />} onClick={() => setPrevzemOdprt(true)}>
                  Stranka je prevzela gume
                </Button>
              )}
              {zaRacun && ureja && (
                <Button component={Link} to={`/racuni/nov?gume=${komplet.id}`} leftSection={<IconReceipt size={18} />}>
                  Izdaj račun
                </Button>
              )}
              {komplet.racunId && (
                <Button
                  component={Link}
                  to={`/racuni/${komplet.racunId}`}
                  variant="default"
                  leftSection={<IconReceipt size={18} />}
                >
                  Račun {komplet.stRacuna}
                </Button>
              )}
            </Group>
          )
        }
      />
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      {komplet?.stanje === 'PREVZET' && (
        <Alert color="gray" variant="light" mb="md">
          Stranka je gume prevzela {datum(komplet.prevzeto)}.
        </Alert>
      )}
      {zaRacun && (
        <Alert color="blue" variant="light" icon={<IconReceipt size={18} />} mb="md">
          Račun še ni izdan: {komplet.postavkaNaziv} ({denar(komplet.postavkaZnesek)} € z DDV).
        </Alert>
      )}
      {komplet?.obrabljene && komplet.sezona && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} mb="md">
          Gume so obrabljene: najmanjši profil je {kolicina(komplet.minProfil)} mm, za{' '}
          {SEZONE[komplet.sezona].naziv.toLowerCase()} gume je meja {komplet.sezona === 'LETNE' ? '1,6' : '3'} mm.
          Predlagajte stranki nove.
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Stranka in vozilo">
            <Stack gap="sm">
              <IzbiraVozila
                izbrano={vozilo}
                izberi={(x) => {
                  setVozilo(x);
                  if (x?.kupecId && !kupec) {
                    void naloziStranko(x.kupecId);
                  }
                }}
                napaka={polja.voziloId}
                obvezno={false}
              />
              <IzbiraOsebe vrsta="kupci" oznaka="Stranka" izbran={kupec} izberi={setKupec} napaka={polja.kupecId} />
              {nalogId && (
                <Text size="sm" c="dimmed">
                  Shranjene ob nalogu{' '}
                  <Anchor component={Link} to={`/delavnica/nalogi/${nalogId}`}>
                    {komplet?.stevilkaNaloga ?? 'odpri'}
                  </Anchor>
                </Text>
              )}
            </Stack>
          </Kartica>
          <Kartica naslov="Hramba">
            <Stack gap="sm">
              <TextInput
                label="Lokacija v skladišču"
                description="Neobvezno, npr. polica-nivo-mesto: A-3-2. Na isti lokaciji je lahko več kompletov."
                value={v.lokacija}
                onChange={(e) => nastavi({ lokacija: velike(e.currentTarget.value) })}
                error={polja.lokacija}
              />
              <DatePickerInput
                label="Shranjeno"
                placeholder="Danes"
                value={v.shranjeno}
                onChange={(d) => nastavi({ shranjeno: datumZaApi(d) ?? null })}
                valueFormat="D. M. YYYY"
                clearable
              />
              <Textarea
                label="Opomba"
                value={v.opomba}
                onChange={(e) => nastavi({ opomba: e.currentTarget.value })}
                error={polja.opomba}
                autosize
                minRows={2}
              />
            </Stack>
          </Kartica>
          <Kartica naslov="Račun">
            <Stack gap="sm">
              <Select
                label="Postavka za račun"
                placeholder="Brez računa"
                description={
                  zaracunan
                    ? `Zaračunano z računom ${komplet?.stRacuna ?? ''}, postavke ni več mogoče zamenjati.`
                    : 'Ni obvezno. Ko je postavka izbrana in shranjena, lahko izdate račun.'
                }
                data={postavke}
                value={v.cenikId}
                onChange={(x) => nastavi({ cenikId: x })}
                error={polja.cenikId}
                disabled={zaracunan || !ureja}
                clearable
                nothingFoundMessage="Ni postavk"
              />
              {zaracunan && (
                <Text size="sm">
                  Račun{' '}
                  <Anchor component={Link} to={`/racuni/${komplet?.racunId}`}>
                    {komplet?.stRacuna}
                  </Anchor>{' '}
                  ({denar(komplet?.postavkaZnesek)} € z DDV)
                </Text>
              )}
              {cenikHotela.data && cenikHotela.data.length === 0 && !zaracunan && (
                <Text size="sm" c="dimmed">
                  Cenik hotela je prazen.{' '}
                  <Anchor component={Link} to="/delavnica/gume/cenik">
                    Dodajte postavke v cenik
                  </Anchor>
                  .
                </Text>
              )}
            </Stack>
          </Kartica>
        </Stack>
        <Stack gap="md">
          <Kartica naslov="Gume">
            <Stack gap="sm">
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Sezona{' '}
                  <Text span c="red">
                    *
                  </Text>
                </Text>
                <SegmentedControl
                  fullWidth
                  data={(Object.keys(SEZONE) as SezonaGum[]).map((s) => ({ value: s, label: SEZONE[s].naziv }))}
                  value={v.sezona ?? ''}
                  onChange={(s) => nastavi({ sezona: s as SezonaGum })}
                  aria-label="Sezona"
                />
                {polja.sezona && (
                  <Text size="xs" c="red" mt={4}>
                    {polja.sezona}
                  </Text>
                )}
              </div>
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                <TextInput
                  label="Dimenzija"
                  placeholder="205/55 R16"
                  value={v.dimenzija}
                  onChange={(e) => nastavi({ dimenzija: velike(e.currentTarget.value) })}
                  error={polja.dimenzija}
                />
                <TextInput
                  label="Indeks nosilnosti in hitrosti"
                  placeholder="91H"
                  value={v.indeks}
                  onChange={(e) => nastavi({ indeks: velike(e.currentTarget.value) })}
                  error={polja.indeks}
                />
                <TextInput
                  label="Znamka"
                  value={v.znamka}
                  onChange={(e) => nastavi({ znamka: e.currentTarget.value })}
                  error={polja.znamka}
                />
                <TextInput
                  label="Vzorec"
                  value={v.vzorec}
                  onChange={(e) => nastavi({ vzorec: e.currentTarget.value })}
                  error={polja.vzorec}
                />
                <TextInput
                  label="DOT (teden in leto)"
                  placeholder="2321"
                  inputMode="numeric"
                  value={v.dot}
                  onChange={(e) => nastavi({ dot: e.currentTarget.value })}
                  error={polja.dot}
                />
                <NumberInput
                  size="md"
                  label="Število gum"
                  value={v.stevilo}
                  onChange={(x) => nastavi({ stevilo: x })}
                  min={1}
                  max={8}
                  allowDecimal={false}
                  error={polja.stevilo}
                />
              </SimpleGrid>
              <Select
                label="Platišča"
                data={PLATISCA}
                value={v.platisca}
                onChange={(x) => nastavi({ platisca: (x as PlatiscaGum) ?? 'BREZ' })}
                allowDeselect={false}
              />
            </Stack>
          </Kartica>
          <Kartica naslov="Profil (mm)">
            <SimpleGrid cols={2} spacing="sm">
              {PROFILI.map(([k, oznaka]) => (
                <NumberInput
                  size="md"
                  key={k}
                  label={oznaka}
                  value={v[k]}
                  onChange={(x) => nastavi({ [k]: x } as Partial<Vnos>)}
                  min={0}
                  max={20}
                  step={0.1}
                  decimalScale={1}
                  decimalSeparator=","
                  error={polja[k]}
                />
              ))}
            </SimpleGrid>
          </Kartica>
        </Stack>
      </SimpleGrid>
      {ureja && (
        <Group justify="space-between" mt="md">
          <Group gap="xs">
            {komplet &&
              (brisanje ? (
                <>
                  <Button color="red" onClick={() => void izbrisi()} loading={dela}>
                    Potrdi izbris
                  </Button>
                  <Button variant="subtle" onClick={() => setBrisanje(false)}>
                    Prekliči
                  </Button>
                </>
              ) : (
                <Button variant="subtle" color="red" onClick={() => setBrisanje(true)}>
                  Izbriši
                </Button>
              ))}
          </Group>
          <Button size="md" onClick={() => void shrani()} loading={dela}>
            {nov ? 'Shrani gume' : 'Shrani'}
          </Button>
        </Group>
      )}
      <Modal opened={prevzemOdprt} onClose={() => setPrevzemOdprt(false)} title="Prevzem gum">
        <Stack gap="sm">
          <Text size="sm">
            Stranka prevzame gume{komplet?.lokacija ? ` z lokacije ${komplet.lokacija}` : ''}.
            {komplet?.zaRacun && ' Račun za hrambo še ni izdan.'}
          </Text>
          <Group justify="flex-end" gap="xs" mt="xs">
            <Button variant="subtle" onClick={() => setPrevzemOdprt(false)} disabled={dela}>
              Prekliči
            </Button>
            <Button onClick={() => void prevzem()} loading={dela}>
              Potrdi prevzem
            </Button>
          </Group>
        </Stack>
      </Modal>
    </ZaslonSeznama>
  );
}
